import React, { useCallback, useEffect, useState, forwardRef } from 'react';
import { Editor } from '@tinymce/tinymce-react';
import PropTypes from 'prop-types';
import { makeStyles } from '@material-ui/core/styles';

import i18n from '../../../i18n';
import richTextEditorConfig from './RichTextEditorConfig';
import './index';
import './skins/skin.min.css';

const useStyles = makeStyles({
  tinymceEditor: {
    '& .tox.tox-tinymce': {
      borderBottomLeftRadius: '0',
      borderBottomRightRadius: '0'
    }
  },
  error: {
    '& .tox.tox-tinymce': {
      borderBottom: '0.125rem solid #f44336'
    }
  }
});

const RichTextEditor = forwardRef((props, ref) => {
  const { editorId, editorHeight, inline = false, onChange, onInit, disabled, value, placeholder, error, maxHeight, minHeight } = props;
  const { toolbar, plugins } = richTextEditorConfig;
  const [editorValue, setEditorValue] = useState(value);

  const setLanguageCode = () => {
    const langCode = i18n.language;
    switch (langCode) {
      case 'zh':
        return 'zh_CN';
      case 'pt':
        return 'pt_PT';
      case 'fr':
        return 'fr_FR';
      case 'ko':
        return 'ko_KR';
      default:
        return langCode;
    }
  };

  const classes = useStyles();

  const onInputChange = useCallback(
    input => {
      setEditorValue(input);
      onChange(input);
    },
    [onChange]
  );

  const onFocus = (e, editor) => {
    const elementFocus = editor.getContainer();
    elementFocus.style.borderBottom = '2px solid #176DC2';
  };

  useEffect(() => {
    setEditorValue(value);
  }, [value]);

  return (
    <div ref={ref} className={`${classes.tinymceEditor} ${error ? classes.error : ''}`}>
      <Editor
        init={{
          ...richTextEditorConfig,
          height: editorHeight,
          min_height: minHeight,
          max_height: maxHeight,
          language: setLanguageCode(),
          placeholder,
          mobile: {
            ...richTextEditorConfig.mobile,
            placeholder
          },
          content_style: `
            .mce-content-body {
              font-family: Arial;
              background: rgba(0, 0, 0, 0.09);
            }
            .mce-content-readonly {
              color: rgba(0, 0, 0, 0.38);
              opacity: 1;
            }
          `
        }}
        disabled={disabled}
        id={editorId}
        inline={inline}
        plugins={plugins}
        toolbar={toolbar}
        onEditorChange={onInputChange}
        value={editorValue}
        onInit={onInit}
        onFocus={onFocus}
      />
    </div>
  );
});

RichTextEditor.defaultProps = {
  editorId: '',
  editorHeight: 200,
  inline: false,
  onChange: e => e,
  onInit: e => e,
  disabled: false,
  value: '',
  maxHeight: 500,
  minHeight: 200
};

RichTextEditor.propTypes = {
  editorId: PropTypes.string,
  editorHeight: PropTypes.number,
  inline: PropTypes.bool,
  onChange: PropTypes.func,
  onInit: PropTypes.func,
  disabled: PropTypes.bool,
  value: PropTypes.string,
  maxHeight: PropTypes.number,
  minHeight: PropTypes.number
};

export default RichTextEditor;
